/* Copyright (c) 2020-present CloudIO Inc */

import type { Platform } from 'componentregistry/platform/Platform';
import type { ColumnType } from '../../../registry/Base.type';
import { CommonProps } from '../../../layout/Sidebar/panels/PropertiesView/CommonProperties';
import {
  DefaultExpressionValue,
  ExpressionValue,
  PropertyDefinition,
  PropertyDefinitions,
} from '../../../registry/Component.type';
import { gridColumnAlignment } from '../../../registry/constants';
import {
  BaseField,
  baseFieldDefaultValue,
  baseFieldProps,
} from '../../TextField/BaseField';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import {
  ToolTip,
  ToolTipDefaultValue,
} from 'componentregistry/utils/commonProps';

export const DEFAULT_COLUMN_WIDTH = 140;

export interface BaseColumn extends Omit<BaseField, 'flex'>, ToolTip {
  filterable?: boolean;
  pinLeft?: boolean;
  pinRight?: boolean;
  sortable?: boolean;
  width?: number;
  flex?: number;
  alignment?: keyof typeof gridColumnAlignment;
  colorExpression?: ExpressionValue;
  bgColorExpression?: ExpressionValue;
  group?: ExpressionValue;
  header1?: ExpressionValue;
  header2?: ExpressionValue;
  header3?: ExpressionValue;
}

export const baseColumnDefaultValue: Required<BaseColumn> = {
  ...baseFieldDefaultValue,
  ...ToolTipDefaultValue,
  insertable: { value: false, type: 'boolean' },
  label: { type: 'text', value: 'Change Me!' },
  updatable: { value: false, type: 'boolean' },
  viewAttribute: '',
  filterable: true,
  pinLeft: false,
  pinRight: false,
  sortable: true,
  width: DEFAULT_COLUMN_WIDTH,
  flex: 0,
  alignment: 'start',
  colorExpression: DefaultExpressionValue,
  bgColorExpression: DefaultExpressionValue,
  group: DefaultExpressionValue,
  header1: DefaultExpressionValue,
  header2: DefaultExpressionValue,
  header3: DefaultExpressionValue,
};

export const formatFunction: PropertyDefinition = {
  category: 'Display',
  label: 'Format Function',
  type: 'controllerFunction',
  alwaysShow: true,
  helpText: `A page controller function to format the value of the column. The function will be called with the props {viewAttribute, value, record}.

The function should return a string.

e.g.
\`\`\`javascript
const NF = {
  USD: new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }),
};

function formatColumnValue({viewAttribute, value, record}) {
  if (viewAttribute === 'salary') {
    let nf = NF[record.currency] ?? NF.USD;
    return '$ ' + nf.format(value); 
  }
  return value;
}
\`\`\`
`,
};

const { flex, ...othersBaseFieldProps } = baseFieldProps;
export const baseColumnProps: PropertyDefinitions<
  Omit<BaseColumn, keyof CommonProps>
> = {
  ...othersBaseFieldProps,
  ...toolTipProps,
  filterable: {
    label: 'Filterable',
    type: 'boolean',
  },
  sortable: {
    label: 'Sortable',
    type: 'boolean',
  },
  width: {
    label: 'Width',
    type: 'number',
    min: 40,
    max: 600,
    step: 10,
    category: 'Layout',
  },
  flex: {
    label: 'Flex',
    type: 'number',
    max: 10,
    helpText:
      'If set, the column will stretch in propotion to the given flex value when the total grid width is less than the screen width.',
    category: 'Layout',
  },
  pinLeft: {
    label: 'Pin Left',
    type: 'boolean',
    category: 'Layout',
  },
  pinRight: {
    label: 'Pin Right',
    type: 'boolean',
    category: 'Layout',
  },
  alignment: {
    label: 'Alignment',
    type: 'select',
    options: gridColumnAlignment,
    category: 'Layout',
  },
  colorExpression: {
    category: 'Look & Feel',
    label: 'Text Color',
    type: 'ColorExpression',
    placeholder: "e.g. '#FFFFFF'",
  },
  bgColorExpression: {
    category: 'Look & Feel',
    label: 'Background Color',
    type: 'ColorExpression',
    placeholder: "e.g. '#157CEC'",
  },
  group: {
    category: 'General',
    label: 'Group Header',
    type: 'expression',
    returnType: async () => 'string',
  },
  header1: {
    category: 'General',
    label: 'Group Header1',
    type: 'expression',
    returnType: async () => 'string',
  },
  header2: {
    category: 'General',
    label: 'Group Header2',
    type: 'expression',
    returnType: async () => 'string',
  },
  header3: {
    category: 'General',
    label: 'Group Header3',
    type: 'expression',
    returnType: async () => 'string',
  },
};

export const allSwichColumns: ColumnType[] = [
  'ActionIconsColumn',
  'AsyncSelectColumn',
  'AttachmentColumn',
  'ButtonColumn',
  'CheckboxColumn',
  'DateColumn',
  'DivColumn',
  'IconColumn',
  'ImageColumn',
  'LOVColumn',
  'LookupSelectColumn',
  'NumberColumn',
  'ProgressIndicatorColumn',
  'SelectColumn',
  'StarColumn',
  'StringColumn',
  'TreeColumn',
  'UserPicColumn',
  'MultiSelectLOVColumn',
];

export function isBaseColumnPropertyVisible({
  appUid,
  pageId,
  itemId,
  prop,
  platform,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  prop: string;
  platform: Platform;
}): boolean {
  const item = platform.getItem(appUid, pageId, itemId);
  if (!item) {
    platform.showError(`Missing column with Item ID: ${itemId}!`);
    return false;
  }
  const parent = platform.getItem(appUid, pageId, item.parent);
  if (!parent) {
    platform.showError(`Missing column parent with Item ID: ${itemId}!`);
    return false;
  }
  if (parent.type === 'SwitchColumnCase') {
    switch (prop) {
      case 'filterable':
      case 'sortable':
      case 'pinLeft':
      case 'pinRight':
      case 'width':
      case 'flex':
      case 'visible':
      case 'visibleCondition':
      case 'responsive':
      case 'maxUniqueVals':
      case 'header1':
      case 'header2':
      case 'header3':
      case 'group':
        return false;
      default:
    }
  } else if (parent.type === 'LOVGrid') {
    switch (prop) {
      case 'pinLeft':
      case 'pinRight':
      case 'insertable':
      case 'updatable':
      case 'header1':
      case 'header2':
      case 'header3':
      case 'group':
        return false;
      default:
    }
  } else if (parent.type === 'DataGrid') {
    if (
      item.type === 'SwitchColumn' ||
      item.type === 'RowSelectionColumn' ||
      item.type === 'RowNumberColumn'
    ) {
      switch (prop) {
        case 'header1':
        case 'header2':
        case 'header3':
        case 'group':
          return false;
        default:
      }
    }
    switch (prop) {
      case 'group':
        return false;
      default:
    }
  } else if (parent.type === 'CanvasGrid') {
    if (item.type === 'NumberColumn') {
      switch (prop) {
        case 'format':
        case 'mask':
          return false;
        default:
      }
    }
    if (item.type === 'ProgressIndicatorColumn') {
      switch (prop) {
        case 'type':
        case 'thickness':
        case 'linearWidth':
        case 'fontSize':
        case 'size':
        case 'color':
        case 'bgcolor':
          return false;
        default:
      }
    }
    if (item.type === 'ButtonColumn') {
      switch (prop) {
        case 'bgColorExpression':
        case 'colorExpression':
        case 'startIcon':
        case 'endIcon':
        case 'iconColor':
        case 'hotKey':
        case 'variant':
        case 'fullWidth':
        case 'disableElevation':
        case 'alignment':
          return false;
        default:
      }
    }
    if (item.type === 'CheckboxColumn') {
      switch (prop) {
        case 'bgColorExpression':
        case 'colorExpression':
        case 'alignment':
          return false;
        default:
      }
    }
    if (item.type === 'IconColumn') {
      switch (prop) {
        case 'commentsItemId':
          return false;
        default:
      }
    }
    if (
      item.type === 'SwitchColumn' ||
      item.type === 'RowSelectionColumn' ||
      item.type === 'RowNumberColumn'
    ) {
      switch (prop) {
        case 'group':
          return false;
        default:
      }
    }
    switch (prop) {
      case 'pinRight':
      case 'filterable':
      case 'maxUniqueVals':
      case 'header1':
      case 'header2':
      case 'header3':
        return false;
      default:
    }
  } else {
    switch (prop) {
      case 'formatFunction':
        return (
          item.type === 'NumberColumn' ||
          item.type === 'StringColumn' ||
          item.type === 'LookupSelectColumn' ||
          item.type === 'SelectColumn'
        );
      default:
    }
  }
  return true;
}
